import React, { useEffect, useState } from 'react';
import {
    Modal,
    Tabs,
    Form,
    Select,
    Button,
    Row,
    Col,
    Radio,
    message,
    Tag,
    Alert,
    Progress,
    Space,
    Typography
} from 'antd';
import {
    ApartmentOutlined,
    PlusOutlined,
    MinusCircleOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    SwapOutlined
} from '@ant-design/icons';
import { GetAulesIdConfiguracio } from '../../../../dao/GetAulesIdConfiguracio';
import { GetAulaRaConfig } from '../../../../dao/GetAulaRaConfig';
import { PostActivitat } from '../../../../dao/jutge/PostActivitat';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

interface Props {
    visible: boolean;
    onClose: (updated: boolean) => void;
    selectedItems: any[];
    userId: string | number;
}

interface AulaGroup {
    id_aula: number | string;
    nom_aula: string;
    items: any[];
}

export const AssignarRAsMassiuModal: React.FC<Props> = ({
    visible,
    onClose,
    selectedItems = [],
    userId
}) => {
    const [loading, setLoading] = useState(false);
    const [savingProgress, setSavingProgress] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<string>('0');
    const [mode, setMode] = useState<'replace' | 'append'>('replace');

    // Grupos de aulas
    const [aulaGroups, setAulaGroups] = useState<AulaGroup[]>([]);
    // Cache de RAs e instrumentos por id_aula: { [id_aula]: { ras: any[], config: any[] } }
    const [aulasData, setAulasData] = useState<Record<string, { ras: any[]; config: any[] }>>({});

    const [formsByAula] = useState<Record<string, any>>({});
    const [form] = Form.useForm();

    useEffect(() => {
        if (!visible || !selectedItems || selectedItems.length === 0) {
            setAulaGroups([]);
            return;
        }

        // Agrupar elementos seleccionados por aula
        const groupsMap = new Map<string, AulaGroup>();
        selectedItems.forEach((it) => {
            const aulaId = String(it.id_aula || '0');
            const aulaNom = it.aula || `Aula #${aulaId}`;
            if (!groupsMap.has(aulaId)) {
                groupsMap.set(aulaId, {
                    id_aula: it.id_aula,
                    nom_aula: aulaNom,
                    items: []
                });
            }
            groupsMap.get(aulaId)!.items.push(it);
        });

        const groups = Array.from(groupsMap.values());
        setAulaGroups(groups);
        setActiveTab('0');

        // Cargar RAs e instrumentos para cada aula detectada
        groups.forEach(async (grp) => {
            const aId = grp.id_aula;
            if (!aId || String(aId) === '0') return;
            try {
                const aulaIdStr = String(aId);
                const [resRas, resConfig] = await Promise.all([
                    GetAulesIdConfiguracio(aulaIdStr, 1),
                    GetAulaRaConfig(aulaIdStr)
                ]);

                let parsedRas: any[] = [];
                if (resRas && Array.isArray(resRas)) {
                    parsedRas = resRas.flatMap((item: any) => item.list || []).map((ra: any) => ({
                        id: ra.id,
                        nom: ra.num,
                        descripcio: ra.desc
                    }));
                }

                setAulasData((prev) => ({
                    ...prev,
                    [String(aId)]: {
                        ras: parsedRas,
                        config: resConfig || []
                    }
                }));
            } catch (e) {
                console.error(`Error loading RAs for aula ${aId}:`, e);
            }
        });
    }, [visible, selectedItems]);

    const handleSaveAll = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            setSavingProgress(0);

            // values tiene la estructura: { ras_by_aula: { [id_aula]: [ { id, instrument } ] } }
            const rasByAula = values.ras_by_aula || {};

            let totalUpdated = 0;
            const totalItems = selectedItems.length;

            for (let i = 0; i < totalItems; i++) {
                const item = selectedItems[i];
                const aulaIdStr = String(item.id_aula);
                const chosenRas = rasByAula[aulaIdStr] || [];

                let finalRas: any[] = [];
                if (mode === 'replace') {
                    finalRas = chosenRas.map((r: any) => ({
                        id: r.id,
                        percentatge: r.percentatge || 0,
                        instrument: r.instrument
                    }));
                } else {
                    // Modo append: conservar existentes y añadir los nuevos sin duplicar por (id + instrument)
                    const existing = (item.ras || []).map((r: any) => ({
                        id: r.id,
                        percentatge: r.percentatge || 0,
                        instrument: r.instrument
                    }));
                    const newOnes = chosenRas.map((r: any) => ({
                        id: r.id,
                        percentatge: r.percentatge || 0,
                        instrument: r.instrument
                    }));
                    const combined = [...existing];
                    newOnes.forEach((nr: any) => {
                        const exists = combined.some((ex: any) => ex.id === nr.id && ex.instrument === nr.instrument);
                        if (!exists) combined.push(nr);
                    });
                    finalRas = combined;
                }

                // Construir payload seguro con todos los campos actuales de la actividad/práctica
                const payload = {
                    id: item.id,
                    nom: item.nom,
                    estat: item.estatr ?? item.estat,
                    id_aula: item.id_aula,
                    is_examen: item.is_examen ? 1 : 0,
                    is_concurs: item.is_concurs ? 1 : 0,
                    is_ide: item.is_ide ? 1 : 0,
                    context: item.context || "",
                    id_usuari: userId,
                    tipus: item.tipus_aula ?? item.tipus ?? 0,
                    id_season: item.id_season || 1,
                    inici: item.inici ? dayjs(item.inici.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null,
                    fin: item.final ? dayjs(item.final.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null,
                    javamons: item.javamons ?? 10,
                    minim: item.minim ?? 0,
                    escena: item.escena || null,
                    inst: "",
                    ras: finalRas
                };

                await PostActivitat(payload);
                totalUpdated++;
                setSavingProgress(Math.round((totalUpdated / totalItems) * 100));
            }

            message.success(`S'han assignat els RAs correctament a ${totalUpdated} elements.`);
            onClose(true);
        } catch (err: any) {
            console.error('Error en assignació massiva de RAs:', err);
            message.error(err?.message || "Error al desar els RAs");
        } finally {
            setLoading(false);
            setSavingProgress(null);
        }
    };

    if (aulaGroups.length === 0) return null;

    const isSingleAula = aulaGroups.length === 1;

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: '#e6f7ff',
                        color: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 18
                    }}>
                        <ApartmentOutlined />
                    </div>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 700 }}>Assignació Massiva de RAs</div>
                        <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400 }}>
                            {selectedItems.length} elements seleccionats en {aulaGroups.length} {aulaGroups.length === 1 ? 'aula' : 'aules'}
                        </div>
                    </div>
                </div>
            }
            open={visible}
            onCancel={() => !loading && onClose(false)}
            footer={[
                <Button key="cancel" onClick={() => onClose(false)} disabled={loading}>
                    Cancel·lar
                </Button>,
                <Button key="save" type="primary" onClick={handleSaveAll} loading={loading} style={{ minWidth: 120 }}>
                    {loading ? 'Desant...' : 'Aplicar a tots'}
                </Button>
            ]}
            width={780}
            destroyOnClose
        >
            <div style={{ marginBottom: 16 }}>
                <div style={{
                    background: '#fcfcfc',
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12
                }}>
                    <div>
                        <span style={{ fontWeight: 600, color: '#262626' }}>Acció sobre RAs existents:</span>
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                            Tria si vols sobreescriure els RAs actuals o bé afegir-los com a nous.
                        </div>
                    </div>
                    <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
                        <Radio.Button value="replace">
                            <SwapOutlined /> Reemplaçar
                        </Radio.Button>
                        <Radio.Button value="append">
                            <PlusOutlined /> Afegir / Sumar
                        </Radio.Button>
                    </Radio.Group>
                </div>

                {!isSingleAula && (
                    <Alert
                        type="info"
                        showIcon
                        message="Selecció multi-aula"
                        description="Els elements pertanyen a aules diferents. Configura a cada pestanya els RAs corresponents a aquella aula."
                        style={{ marginBottom: 16, borderRadius: 8 }}
                    />
                )}

                <Form form={form} layout="vertical">
                    {isSingleAula ? (
                        // UNA SOLA AULA DIRECTA
                        renderAulaConfigForm(aulaGroups[0], aulasData[String(aulaGroups[0].id_aula)] || { ras: [], config: [] }, form)
                    ) : (
                        // MÚLTIPLES AULAS EN PESTAÑAS
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            type="card"
                            items={aulaGroups.map((grp, idx) => ({
                                key: String(idx),
                                label: (
                                    <span>
                                        {grp.nom_aula} <Tag color="blue" style={{ marginLeft: 4 }}>{grp.items.length}</Tag>
                                    </span>
                                ),
                                children: renderAulaConfigForm(grp, aulasData[String(grp.id_aula)] || { ras: [], config: [] }, form)
                            }))}
                        />
                    )}
                </Form>

                {savingProgress !== null && (
                    <div style={{ marginTop: 16 }}>
                        <Progress percent={savingProgress} status="active" />
                    </div>
                )}
            </div>
        </Modal>
    );
};

function renderAulaConfigForm(
    group: AulaGroup,
    aulaInfo: { ras: any[]; config: any[] },
    form: any
) {
    const { ras, config } = aulaInfo;
    const aulaIdKey = String(group.id_aula);

    return (
        <div style={{ padding: '8px 4px' }}>
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: '#595959' }}>
                    S'aplicaran els RAs a <b>{group.items.length}</b> elements:
                </span>
                <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                    {group.items.map((it: any) => it.nom).join(', ').substring(0, 60)}...
                </span>
            </div>

            <Form.List name={['ras_by_aula', aulaIdKey]}>
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name, ...restField }) => (
                            <Row
                                key={key}
                                gutter={10}
                                align="middle"
                                style={{
                                    marginBottom: 10,
                                    background: '#fafafa',
                                    border: '1px solid #e8e8e8',
                                    padding: '10px 12px',
                                    borderRadius: 8
                                }}
                            >
                                <Col span={12}>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'id']}
                                        label="Resultat d'Aprenentatge (RA)"
                                        rules={[{ required: true, message: 'Selecciona RA' }]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Select
                                            placeholder="Selecciona RA..."
                                            onChange={() => {
                                                const current = form.getFieldValue(['ras_by_aula', aulaIdKey]) || [];
                                                if (current[name]) {
                                                    current[name].instrument = undefined;
                                                    form.setFieldsValue({
                                                        ras_by_aula: {
                                                            ...form.getFieldValue('ras_by_aula'),
                                                            [aulaIdKey]: current
                                                        }
                                                    });
                                                }
                                            }}
                                        >
                                            {ras.map((ra: any) => (
                                                <Select.Option key={ra.id} value={ra.id}>
                                                    <b>{ra.nom}</b> - {ra.descripcio}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col span={10}>
                                    <Form.Item
                                        noStyle
                                        shouldUpdate={(prev, curr) => {
                                            const pId = prev.ras_by_aula?.[aulaIdKey]?.[name]?.id;
                                            const cId = curr.ras_by_aula?.[aulaIdKey]?.[name]?.id;
                                            return pId !== cId;
                                        }}
                                    >
                                        {() => {
                                            const currentRas = form.getFieldValue(['ras_by_aula', aulaIdKey]) || [];
                                            const currentRaId = currentRas[name]?.id;
                                            const currentRaObj = ras.find((r: any) => r.id === currentRaId);
                                            const raName = currentRaObj ? currentRaObj.nom : '';

                                            const instruments = (currentRaObj && config)
                                                ? config.filter((c: any) =>
                                                    c.nom_ra === raName ||
                                                    c.nom_ra === `RA${raName}` ||
                                                    (c.nom_ra && c.nom_ra.replace(/^RA/, '') === raName)
                                                ).map((c: any) => c.instrument)
                                                : [];

                                            const uniqueInstruments = Array.from(new Set(instruments));

                                            return (
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'instrument']}
                                                    label="Instrument d'Avaluació"
                                                    rules={[{ required: true, message: 'Selecciona Instrument' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Select
                                                        placeholder="Instrument..."
                                                        disabled={!currentRaId}
                                                    >
                                                        {uniqueInstruments.map((inst: any) => (
                                                            <Select.Option key={inst} value={inst}>
                                                                {inst}
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            );
                                        }}
                                    </Form.Item>
                                </Col>

                                <Col span={2} style={{ textAlign: 'center' }}>
                                    <MinusCircleOutlined
                                        onClick={() => remove(name)}
                                        style={{ color: '#ff4d4f', fontSize: 18, cursor: 'pointer', marginTop: 24 }}
                                    />
                                </Col>
                            </Row>
                        ))}

                        <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            style={{ borderRadius: 6, marginTop: 4 }}
                        >
                            Afegir RA a aquesta aula
                        </Button>
                    </>
                )}
            </Form.List>
        </div>
    );
}

export default AssignarRAsMassiuModal;
